import React from "react";
import { Calendar } from "../../assets/icons/calendar";
import { Shortcut } from "../../@types";
import { dashboardDefaultShortcuts } from "../../constants/helpServices";
import { DownArrow } from "../../assets/icons/DownArrow";
import { formatDate } from "../../utils";

type RangeGenerator = () => [Date, Date];
interface DatePickerWithShortcutsProps {
  shortcuts?: Shortcut[];
  onRangeSelect: (range: [Date, Date]) => void;
  selected: [Date, Date] | null;
  onClose: () => void;
  onApplyButtonClick: (startDate: Date, endDate: Date) => Promise<void>;
  fetchData?: () => void;
  startDate?: Date | null;
  endDate?: Date | null;
  close?: boolean;
}

interface Props {
  showCalendar: boolean;
  handleCalendarToggle: () => void;
  selectedLabel: string;
  shortcuts?: Shortcut[];
  startDate?: Date | null;
  endDate?: Date | null;
  setSelectedLabel: React.Dispatch<React.SetStateAction<string>>;
  onApplyButtonClick: (startDate: Date, endDate: Date) => Promise<void>;
  onRangeSelect: (range: [Date, Date]) => void;
  selected: [Date, Date] | null;
}
const DatePickerComponent = ({
  shortcuts = dashboardDefaultShortcuts,
  showCalendar,
  handleCalendarToggle,
  selectedLabel,
  startDate: start,
  endDate: end,
  setSelectedLabel,
  onApplyButtonClick,
  onRangeSelect,
  selected,
}: Props) => {
  const [startDate, setStartDate] = React.useState<Date | null>(
    start ?? new Date(),
  );
  const [endDate, setEndDate] = React.useState<Date | null>(end ?? new Date());
  const [isOpen, setIsOpen] = React.useState(false);
  const [, setSelected] = React.useState<[Date, Date] | null>(null);
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleShortcutClick = async (
    rangeGenerator: RangeGenerator,
    index: number,
    label: string,
  ) => {
    const range: [Date, Date] = rangeGenerator();
    setSelected(range);
    setSelectedLabel(label);
    setStartDate(range[0]);
    setEndDate(range[1]);
    setActiveIndex(index);

    if (range[0] && range[1]) {
      await onApplyButtonClick(range[0], range[1]);
    } else if (range[0] && !range[1]) {
      //@ts-ignore
      await onApplyButtonClick(range[0], null);
    }
    setIsOpen(false);
  };

  React.useEffect(() => {
    if (selected) {
      setStartDate(selected[0]);
      setEndDate(selected[1]);
    }
  }, [selected]);
  return (
    <>
      {/* Desktop */}
      <div className="relative inline-block text-left">
        <div className="md:justify-between cursor-pointer md:flex hidden items-center border rounded-lg border-[#D0D5DD] px-2">
          <span className="px-2">
            <Calendar color={"#1D2939"} />
          </span>
          <span onClick={toggleDropdown} className="px-2 pr-4 text-[#1D2939] ">
            {selectedLabel.includes("-")
              ? selectedLabel
              : `${formatDate(new Date(startDate ?? "").toDateString())} - ${formatDate(new Date(endDate ?? "").toDateString())}`}
          </span>
          <div
            className={`flex items-center justify-center ${selectedLabel.includes("-") || "border-l border-[#D0D5DD]"}`}
          >
            <button
              onClick={toggleDropdown}
              className={`text-[#1D2939] text-[16px] px-2 h-[48px] space-x-2 right-0 flex font-medium justify-center items-center z-0 `}
              value={selectedLabel}
            >
              {selectedLabel.includes("-") || <span>{selectedLabel}</span>}
              <DownArrow />
            </button>
          </div>
        </div>
        {isOpen && (
          <div className="absolute md:flex hidden right-0 mt-2 w-56 bg-white z-50 border rounded-lg shadow-lg">
            <ul className="p-2 w-full">
              <li
                onClick={() => {
                  setIsOpen(false);
                  handleCalendarToggle();
                }}
                className="px-2 text-[16px] py-2 w-full hover:bg-[#F2F4F7] hover:rounded-lg text-[#344054] font-medium cursor-pointer"
              >
                Custom Date
              </li>
              {shortcuts.map((shortcut, index) => (
                <li
                  key={index}
                  onClick={() => {
                    handleShortcutClick(shortcut.range, index, shortcut.label);
                  }}
                  className="px-2 text-[16px] py-2 w-full hover:bg-[#F2F4F7] rounded-lg text-[#344054] font-medium cursor-pointer"
                  style={
                    startDate &&
                    endDate &&
                    shortcut.range()[0].getTime() === startDate.getTime() &&
                    shortcut.range()[1].getTime() === endDate.getTime()
                      ? { backgroundColor: "#F2F4F7" }
                      : {}
                  }
                >
                  {shortcut.label}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Mobile */}
      <div className="md:hidden mb-[1em]">
        <div className="relative">
          <div
            onClick={toggleDropdown}
            className="bg-white relative flex items-center justify-between gap-3 border border-[#D0D5DD] rounded-md  h-[48px] w-full"
            style={{ cursor: showCalendar ? "pointer" : "default" }}
          >
            <div className="ml-[0.5em] z-0" onClick={toggleDropdown}>
              <Calendar />
            </div>

            <div
              className={`text-gray-800 text-[16px] rounded-md text-center z-0 mb-[-2px] w-[350px]`}
            >
              {formatDate(new Date(startDate ?? "").toDateString())} -{" "}
              {formatDate(new Date(endDate ?? "").toDateString())}
            </div>
            <span className="pr-5">
              <DownArrow />
            </span>
          </div>

          {isOpen && (
            <div className="absolute md:hidden right-0 mt-2 w-56 bg-white z-50 border rounded-lg shadow-lg">
              <ul className="p-2">
                <li
                  onClick={() => {
                    setIsOpen(false);
                    handleCalendarToggle();
                  }}
                  className="px-2 text-[16px] py-2 hover:bg-[#F2F4F7] hover:rounded-lg text-[#344054] font-medium cursor-pointer"
                >
                  Custom Date
                </li>
                {shortcuts.map((shortcut, index) => (
                  <li
                    key={index}
                    onClick={() =>
                      handleShortcutClick(shortcut.range, index, shortcut.label)
                    }
                    className="px-2 text-[16px] py-2 hover:bg-[#F2F4F7] rounded-lg text-[#344054] font-medium cursor-pointer"
                    style={
                      startDate &&
                      endDate &&
                      shortcut.range()[0].getTime() === startDate.getTime() &&
                      shortcut.range()[1].getTime() === endDate.getTime()
                        ? { backgroundColor: "#F2F4F7" }
                        : {}
                    }
                  >
                    {shortcut.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DatePickerComponent;
